import React, { useMemo, useRef, useState } from 'react';
import { Card, AgGridTable, Button } from 'components/common';
import { FiDownload, FiList, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const CO_STATUS_STYLES = {
  accepted:         'tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-200',
  accepted_revise:  'tw-bg-teal-100 tw-text-teal-800 tw-border tw-border-teal-200',
  sent:             'tw-bg-blue-100 tw-text-blue-800 tw-border tw-border-blue-200',
  submitted:        'tw-bg-primary-100 tw-text-primary-800 tw-border tw-border-primary-200',
  rejected:         'tw-bg-red-100 tw-text-red-800 tw-border tw-border-red-200',
  requested_revise: 'tw-bg-amber-100 tw-text-amber-800 tw-border tw-border-amber-200',
  rejected_and_closed: 'tw-bg-slate-200 tw-text-slate-700 tw-border tw-border-slate-300'
};

const getCoStatusStyle = (status) =>
  CO_STATUS_STYLES[status] || 'tw-bg-gray-100 tw-text-gray-700 tw-border tw-border-gray-200';

const formatStatus = (status) =>
  (status || 'N/A').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const CoStatusCell = ({ value }) => (
  <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-whitespace-nowrap ${getCoStatusStyle(value)}`}>
    {formatStatus(value)}
  </span>
);

CoStatusCell.propTypes = { value: PropTypes.string };
CoStatusCell.defaultProps = { value: null };

// Pagination with page-number buttons and ellipsis
const Pagination = ({ current, total, totalRecords, perPage, onChange }) => {
  if (!total || total <= 1) return null;

  const getPages = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const left  = Math.max(2, current - 2);
    const right = Math.min(total - 1, current + 2);
    const pages = [1];
    if (left > 2)          pages.push('…');
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  };

  const startRow = (current - 1) * perPage + 1;
  const endRow   = Math.min(current * perPage, totalRecords);

  return (
    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3 tw-border-t tw-border-gray-200 tw-bg-gray-50">
      <p className="tw-text-xs tw-text-gray-500">
        Showing <span className="tw-font-semibold tw-text-gray-700">{startRow}–{endRow}</span> of{' '}
        <span className="tw-font-semibold tw-text-gray-700">{totalRecords}</span> records
      </p>

      <div className="tw-flex tw-items-center tw-gap-1">
        <button
          onClick={() => onChange(current - 1)}
          disabled={current === 1}
          className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors"
        >
          <FiChevronLeft size={13} />
        </button>

        {getPages().map((page, i) =>
          page === '…' ? (
            <span key={`e${i}`} className="tw-w-8 tw-text-center tw-text-gray-400 tw-text-sm tw-select-none">…</span>
          ) : (
            <button
              key={page}
              onClick={() => onChange(page)}
              className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${
                page === current
                  ? 'tw-bg-blue-600 tw-text-white tw-shadow-sm'
                  : 'tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50'
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onChange(current + 1)}
          disabled={current === total}
          className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors"
        >
          <FiChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  current:      PropTypes.number.isRequired,
  total:        PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  perPage:      PropTypes.number.isRequired,
  onChange:     PropTypes.func.isRequired
};

const DetailedChangeOrderList = ({ data, pagination, loading, error, onPageChange, onRequestExport }) => {

  const gridApiRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const columnDefs = useMemo(() => [
    {
      headerName: '#',
      valueGetter: 'node.rowIndex + 1',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      pinned: 'left',
      filter: false,
      sortable: false
    },
    {
      field: 'deal_id',
      headerName: 'Deal ID',
      width: 140,
      minWidth: 120,
      filter: false,
    },
    {
      field: 'job_number',
      headerName: 'Job Number',
      width: 130,
      minWidth: 120,
      filter: false,
      cellStyle: { fontWeight: '500', color: '#1f2937' },
      cellRenderer: ({ value, data }) => (
        <Link to={`/crm/projects/${data?.deal_id}`} target="_blank" className="tw-text-blue-600 hover:tw-text-blue-800 hover:tw-no-underline">
          {value || '—'}
        </Link>
      )
    },
    {
      field: 'project_name',
      headerName: 'Project Name',
      width: 200,
      minWidth: 150,
      filter: false,
      cellRenderer: CoStatusCell
    },
    {
      field: 'building_name',
      headerName: 'Building Name',
      width: 200,
      minWidth: 150,
      filter: false,
      cellRenderer: CoStatusCell
    },
    {
      field: 'co',
      headerName: 'CO#',
      width: 100,
      minWidth: 80,
      filter: false,
      cellRenderer: CoStatusCell
    },
    {
      field: 'co_status',
      headerName: 'CO Status',
      width: 160,
      minWidth: 130,
      filter: false,
      cellRenderer: CoStatusCell
    },
    {
      field: 'entry_date',
      headerName: 'Entry Date',
      width: 140,
      minWidth: 120,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    },
    {
      field: 'building_status',
      headerName: 'Building Status',
      width: 170,
      minWidth: 150,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    },
    {
      field: 'cse_head',
      headerName: 'CSE Incharge',
      width: 150,
      minWidth: 140,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    },
    {
      field: 'sales_unit_name',
      headerName: 'Sales Unit',
      width: 130,
      minWidth: 120,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    },
    {
      field: 'sales_unit_region',
      headerName: 'Sales Region',
      flex: 1,
      minWidth: 200,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    },
    {
      field: 'user_name',
      headerName: 'User',
      width: 140,
      minWidth: 110,
      filter: false,
      valueFormatter: ({ value }) => value || '—'
    }
  ], []);

  const handleExportExcel = async () => {
    if (isExporting || !onRequestExport) return;
    setIsExporting(true);
    try {
      await onRequestExport();
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold tw-text-sm">Error loading change orders</p>
        <p className="tw-text-red-500 tw-text-xs tw-mt-1">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  const totalPages   = pagination?.total_pages   || 0;
  const currentPage  = pagination?.current_page  || 1;
  const totalRecords = pagination?.total_records || (data || []).length;
  const perPage      = pagination?.per_page       || 50;

  return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      {/* Header */}
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-200 tw-bg-gradient-to-r tw-from-gray-50 tw-to-white tw-flex tw-flex-col tw-gap-3">
        {/* Title row */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <div className="tw-w-8 tw-h-8 tw-rounded tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
              <FiList className="tw-text-blue-600" size={16} />
            </div>
            <div>
              <h3 className="tw-text-base tw-font-bold tw-text-gray-900">Detailed Change Orders</h3>
              <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                {totalRecords > 0
                  ? `${totalRecords.toLocaleString()} records · page ${currentPage} of ${totalPages}`
                  : 'No records'}
                {totalPages > 1 && (
                  <span className="tw-ml-1 tw-text-gray-400">— use sidebar filters to narrow results</span>
                )}
              </p>
            </div>
          </div>

          <Button
            variant="secondary"
            leftIcon={FiDownload}
            loading={isExporting}
            disabled={!data || data.length === 0}
            onClick={handleExportExcel}
          >
            {isExporting ? 'Exporting…' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Grid */}
      <AgGridTable
        rowData={data || []}
        columnDefs={columnDefs}
        serverSide={false}
        filter={false}
        sort={true}
        pagination={false}
        loading={loading}
        rowHeight={38}
        headerHeight={44}
        height="520px"
        enableCellTextSelection={true}
        onGridReady={(params) => { gridApiRef.current = params.api; }}
      />

      {/* Page-number pagination */}
      {totalPages > 1 && (
        <Pagination
          current={currentPage}
          total={totalPages}
          totalRecords={totalRecords}
          perPage={perPage}
          onChange={onPageChange}
        />
      )}
    </Card>
  );
};

DetailedChangeOrderList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.shape({
    current_page:  PropTypes.number,
    total_pages:   PropTypes.number,
    total_records: PropTypes.number,
    per_page:      PropTypes.number
  }),
  loading:           PropTypes.bool,
  error:             PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onPageChange:      PropTypes.func,
  onRequestExport:   PropTypes.func,
  jobNumber:         PropTypes.string,
  onJobNumberChange: PropTypes.func
};

DetailedChangeOrderList.defaultProps = {
  data:              [],
  pagination:        null,
  loading:           false,
  error:             null,
  onPageChange:      null,
  onRequestExport:   null,
  jobNumber:         '',
  onJobNumberChange: null
};

export default DetailedChangeOrderList;
